import React, { useState, useContext, useEffect, useRef } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { LanguageContext } from "../LanguageContext"; // jeśli masz
ChartJS.register(ArcElement, Tooltip, Legend);

function LegendTooltip({ x, y, text }) {
  if (!text) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: y + 12,
        left: x + 12,
        background: "rgba(50,50,50,0.9)",
        color: "#fff",
        padding: "6px 10px",
        borderRadius: "8px",
        fontSize: "0.85rem",
        pointerEvents: "none",
        zIndex: 1000,
        boxShadow: "0px 2px 8px rgba(0,0,0,0.3)",
        
        maxWidth: "220px",      // 👈 ograniczenie szerokości
        whiteSpace: "normal",   // 👈 pozwala łamać wiersze
        wordBreak: "keep-all",  // 👈 łamie tylko na spacjach
      }}
    >
      {text}
      <div
        style={{
          position: "absolute",
          top: -6,
          left: 8,
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderBottom: "6px solid rgba(50,50,50,0.9)",
        }}
      />
    </div>
  );
}


function PieChartComponent({ chartdata }) {
  const { t } = useContext(LanguageContext);
  const [tooltip, setTooltip] = useState({ x: 0, y: 0, text: "" });
  const chartRef = useRef(null);

  useEffect(() => {
    const triggerResize = () => {
      if (chartRef.current) {
        chartRef.current.resize();
        chartRef.current.update("none");
      }
    };

    let rafId = null;
    const requestResize = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        triggerResize();
      });
    };

    requestResize();
    const timeoutId = setTimeout(requestResize, 120);

    window.addEventListener("resize", requestResize);
    window.addEventListener("orientationchange", requestResize);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      clearTimeout(timeoutId);
      window.removeEventListener("resize", requestResize);
      window.removeEventListener("orientationchange", requestResize);
    };
  }, []);

  const hasV2States =
    chartdata.state0NoProcess !== undefined ||
    chartdata.state1Recognition !== undefined ||
    chartdata.state2Recognized !== undefined ||
    chartdata.state3Unrecognized !== undefined ||
    chartdata.state4CleaningStart !== undefined ||
    chartdata.state5CleaningContinue !== undefined;

  const noProcess = hasV2States ? (chartdata.state0NoProcess ?? 0) : (chartdata.noProcess ?? 0);
  const recognitionProcess = hasV2States ? (chartdata.state1Recognition ?? 0) : 0;
  const unrecognizedProcess = hasV2States
    ? (chartdata.state3Unrecognized ?? 0)
    : (chartdata.nonRProcess ?? 0);
  const rProcess = hasV2States ? (chartdata.state2Recognized ?? 0) : (chartdata.rProcess ?? 0);
  const cleaning = hasV2States
    ? (chartdata.state4CleaningStart ?? 0) + (chartdata.state5CleaningContinue ?? 0)
    : (chartdata.cleaning ?? 0);

  const values = [
    noProcess,
    recognitionProcess,
    unrecognizedProcess,
    rProcess,
    cleaning,
  ];

  const data = {
    labels: [
      t("no_proces"),
      t("process_recognition"),
      t("process_unrecognized"),
      t("r_proces"),
      t("cleaning"),
    ],
    datasets: [
      {
        data: values,
        backgroundColor: [
          "#ff0000",
          "#ff9900",
          "#cccc00",
          "#33cc33",
          "#66ccff",
        ],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: {
      padding: {
        bottom: 10,
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 10,
          generateLabels: function (chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i] || 0;
                const formattedTime = formatTime(value, t);
                return {
                  text: `${label} (${formattedTime})`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
        onHover: (event, legendItem, legend) => {
          const index = legendItem.index;
          //zależnie od numeru index wybierz odpowiedni opis z listy tłumaczeń
          let description = "";
          switch(index) {
            case 0:
              description = t("no_proces_desc");
              break;
            case 1:
              description = t("process_recognition_desc");
              break;
            case 2:
              description = t("process_unrecognized_desc");
              break;
            case 3:
              description = t("r_proces_desc");
              break;
            case 4:
              description = t("cleaning_desc");
              break;
            default:
              description = "";
          }

          const mouseX = event.native?.clientX ?? 0;
          const mouseY = event.native?.clientY ?? 0;

          setTooltip({
            x: mouseX-25,
            y: mouseY,
            text: description,
          });
        },
        onLeave: () => {
          setTooltip({ x: 0, y: 0, text: "" });
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.parsed;
            const label = context.label;
            return `${label}: ${formatTime(value, t)}`;
          },
        },
      },
    },
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        position: "relative",
      }}
      onMouseLeave={() => setTooltip({ x: 0, y: 0, text: "" })}
      onPointerLeave={() => setTooltip({ x: 0, y: 0, text: "" })}
    >
      <Pie ref={chartRef} data={data} options={options} redraw />
      <LegendTooltip x={tooltip.x} y={tooltip.y} text={tooltip.text} />
    </div>
  );
}

export default React.memo(PieChartComponent);

function formatTime(value, t) {
  if (value < 60) {
    return `${value} ${t("minutes")}`;
  } else if (value < 1440) {
    const hours = (value / 60).toFixed(1);
    return `${hours} ${t("hours")}`;
  } else {
    const days = (value / 1440).toFixed(1);
    return `${days} ${t("days")}`;
  }
}
